import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from 'context/CartContext';
import { useStore } from 'context/StoreContext';
import useCategories from 'hooks/useCategories';
import Container from './Container';
import Drawer from './Drawer';
import LanguageSwitch from './LanguageSwitch';
import Skeleton from './Skeleton';
import { BagIcon, CloseIcon, MenuIcon, SearchIcon } from './icons';

function AnnouncementBar() {
  const { theme } = useStore();
  const { enabled, text } = theme.announcement;
  if (!enabled || !text) return null;
  return (
    <div className="bg-primary text-primary-contrast">
      <Container className="flex min-h-[36px] items-center justify-center py-2 text-center text-[13px] leading-snug">
        <p>{text}</p>
      </Container>
    </div>
  );
}

export function Brand({ className = '' }) {
  const { identity, isPlaceholder } = useStore();
  const [logoFailed, setLogoFailed] = useState(false);

  // Reserve the final height so the header never jumps when the profile arrives.
  if (isPlaceholder) {
    return (
      <div className={`flex h-10 items-center ${className}`} aria-hidden="true">
        <Skeleton className="h-6 w-28" />
      </div>
    );
  }

  const showLogo = identity.logoUrl && !logoFailed;
  return (
    <Link to="/" className={`flex h-10 items-center text-ink ${className}`} aria-label={identity.name}>
      {showLogo ? (
        <img
          src={identity.logoUrl}
          alt=""
          className="h-8 w-auto max-w-[10rem] object-contain"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="font-display max-w-[12rem] truncate text-[1.65rem] leading-none md:max-w-[16rem]">
          {identity.name}
        </span>
      )}
    </Link>
  );
}

function SearchPanel({ open, onClose, categories }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const urlQuery = pathname === '/cari' ? params.get('q') || '' : '';
  const [value, setValue] = useState(urlQuery);
  const input = useRef(null);
  const inputId = useId();

  useEffect(() => setValue(urlQuery), [urlQuery]);
  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  const go = (q) => {
    const query = q.trim();
    navigate(query ? `/cari?q=${encodeURIComponent(query)}` : '/cari');
    onClose();
  };

  if (!open) return null;
  return (
    <div className="absolute inset-x-0 top-full z-30 border-b border-border bg-background shadow-[0_18px_30px_-20px_rgb(0_0_0/0.25)]">
      <Container className="py-5">
        <form
          role="search"
          action="/cari"
          method="get"
          onSubmit={(e) => {
            e.preventDefault();
            go(value);
          }}
          className="flex items-center gap-3"
        >
          <label htmlFor={inputId} className="sr-only">
            {t('header.searchLabel')}
          </label>
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted" />
            <input
              ref={input}
              id={inputId}
              name="q"
              type="search"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('header.searchPlaceholder')}
              autoComplete="off"
              enterKeyHint="search"
              className="min-h-[52px] w-full rounded-btn border border-border bg-surface pl-12 pr-4 text-base text-ink placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-ink/5"
          >
            <CloseIcon className="h-5 w-5" />
            <span className="sr-only">{t('header.closeSearch')}</span>
          </button>
        </form>
        {categories.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted">{t('header.browseCategories')}</span>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/kategori/${c.slug}`}
                onClick={onClose}
                className="rounded-btn border border-border px-3 py-1.5 text-ink transition hover:border-ink"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}

function MobileMenu({ open, onClose, categories }) {
  const { t } = useTranslation();
  const { identity, infoPages, showArticles } = useStore();
  const pages = infoPages.filter((p) => ['tentang-kami', 'kontak', 'faq', 'pengiriman-pengembalian'].includes(p.slug));
  const item = 'flex min-h-[52px] items-center justify-between border-b border-border text-[17px]';
  return (
    <Drawer open={open} onClose={onClose} side="left" title={identity.name}>
      <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-3">
        <span className="eyebrow">{t('language.label')}</span>
        <LanguageSwitch size="touch" />
      </div>
      <nav aria-label={t('header.mainNav')} className="px-5 pb-8">
        <p className="eyebrow mb-1 mt-6">{t('header.shop')}</p>
        <ul>
          <li>
            <Link to="/kategori/semua" className={item} onClick={onClose}>
              {t('header.allProducts')}
            </Link>
          </li>
          {categories.map((c) => (
            <li key={c.slug}>
              <Link to={`/kategori/${c.slug}`} className={item} onClick={onClose}>
                {c.name}
                <span className="text-sm text-muted">{c.productCount}</span>
              </Link>
            </li>
          ))}
          {showArticles && (
            <li>
              <Link to="/artikel" className={item} onClick={onClose}>
                {t('articles.nav')}
              </Link>
            </li>
          )}
        </ul>
        <p className="eyebrow mb-1 mt-8">{t('header.help')}</p>
        <ul>
          <li>
            <Link to="/pesanan-saya" className={item} onClick={onClose}>
              {t('header.myOrders')}
            </Link>
          </li>
          {pages.length > 0 && (
            <>
              {pages.map((p) => (
                <li key={p.slug}>
                  <Link to={`/halaman/${p.slug}`} className={item} onClick={onClose}>
                    {t(p.titleKey)}
                  </Link>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>
    </Drawer>
  );
}

const navLinkClass = ({ isActive }) =>
  `relative flex h-full items-center text-[14px] font-medium transition-colors hover:text-ink ${
    isActive
      ? 'text-ink after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:bg-ink'
      : 'text-ink/75'
  }`;

export default function Header() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { count, openDrawer } = useCart();
  const { showArticles } = useStore();
  const categories = useCategories();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const header = useRef(null);

  // Close panels after navigating.
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [pathname, search]);

  // Escape or a click outside closes the search panel.
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setSearchOpen(false);
    const onClick = (e) => header.current && !header.current.contains(e.target) && setSearchOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [searchOpen]);

  const iconBtn =
    'relative inline-flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5';

  return (
    <>
      <AnnouncementBar />
      <header ref={header} className="sticky top-0 z-40 border-b border-border bg-background">
        <Container className="relative grid h-[60px] grid-cols-[1fr_auto_1fr] items-center gap-2 lg:h-[72px] lg:grid-cols-[auto_1fr_auto] lg:gap-10">
          <div className="flex items-center lg:hidden">
            <button
              type="button"
              className={`${iconBtn} -ml-2.5`}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon className="h-6 w-6" />
              <span className="sr-only">{t('header.openMenu')}</span>
            </button>
          </div>

          <Brand className="justify-self-center lg:justify-self-start" />

          <nav aria-label={t('header.mainNav')} className="hidden h-full justify-center lg:flex">
            <ul className="flex h-full items-stretch gap-7 xl:gap-9">
              {categories.map((c) => (
                <li key={c.slug}>
                  <NavLink to={`/kategori/${c.slug}`} className={navLinkClass}>
                    {c.name}
                  </NavLink>
                </li>
              ))}
              {showArticles && (
                <li className="flex items-stretch before:my-auto before:mr-7 before:h-4 before:w-px before:bg-border xl:before:mr-9">
                  <NavLink to="/artikel" className={navLinkClass}>
                    {t('articles.nav')}
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>

          <div className="flex items-center justify-self-end">
            <Link
              to="/pesanan-saya"
              className="mr-3 hidden min-h-[44px] items-center text-[14px] font-medium text-ink/75 underline-offset-4 transition-colors hover:text-ink hover:underline lg:inline-flex"
            >
              {t('header.myOrders')}
            </Link>
            <LanguageSwitch className="mr-2 hidden lg:inline-flex" />
            <button
              type="button"
              className={iconBtn}
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((o) => !o)}
            >
              <SearchIcon className="h-[22px] w-[22px]" />
              <span className="sr-only">{t('header.openSearch')}</span>
            </button>
            <button
              type="button"
              className={`${iconBtn} -mr-2.5`}
              onClick={openDrawer}
              aria-label={t('header.cartLabel', { count })}
            >
              <BagIcon className="h-[22px] w-[22px]" />
              {count > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[11px] font-bold tabular-nums text-accent-contrast"
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          </div>
        </Container>
        <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} categories={categories} />
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} categories={categories} />
    </>
  );
}
